import { useEffect, useState, useCallback } from "react";
import { applicationService, updateApplicationWithCourseData } from "services";
import { DOCUMENT_GROUPS } from "types";

type Document = {
  id: string;
  documentType: string;
  fileName: string;
  [key: string]: any;
};

type GroupedDocuments = Record<string, Document[]>;

type UseApplicationDetailsProps = {
  institutionId?: string;
  applicationId?: string;
  shouldFetchNotes?: boolean;
};

const useApplicationDetails = ({
  institutionId,
  applicationId,
  shouldFetchNotes = true,
}: UseApplicationDetailsProps) => {
  const [details, setDetails] = useState<any>(null);
  const [notes, setNotes] = useState<any>(null);
  const [documents, setDocuments] = useState<any[]>([]);
  const [groupedDocuments, setGroupedDocuments] = useState<GroupedDocuments>(
    {},
  );
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const mapDocumentToGroups = (document: Document[]) => {
    const grouped: Record<string, Document[]> = {};

    document.forEach((doc) => {
      const groupEntry = Object.entries(DOCUMENT_GROUPS).find(
        ([_, g]) =>
          Array.isArray(g.types) && g.types.includes(doc.documentType as any),
      );

      const groupName = groupEntry ? groupEntry[0] : "Other";

      if (!grouped[groupName]) grouped[groupName] = [];
      grouped[groupName].push(doc);
    });

    return grouped;
  };

  const fetchApplicationData = useCallback(async () => {
    if (!institutionId || !applicationId) return;

    setIsLoading(true);
    setErrorMessage(null);

    try {
      const application = await applicationService.getApplicationDetails(
        institutionId,
        applicationId,
      );

      const applicationWithCourse =
        await updateApplicationWithCourseData(application);

      setDetails(applicationWithCourse);

      const documentsResponse =
        await applicationService.getApplicationDocuments(applicationId);
      setDocuments(documentsResponse);
      setGroupedDocuments(mapDocumentToGroups(documentsResponse));

      if (shouldFetchNotes) {
        const applicationNotes = await applicationService.getApplicationNotes(
          institutionId,
          applicationId,
        );
        setNotes(applicationNotes);
      }
    } catch (error) {
      console.error("Failed to load application data:", error);
      setErrorMessage("Failed to load application data");
    } finally {
      setIsLoading(false);
    }
  }, [institutionId, applicationId, shouldFetchNotes]);

  useEffect(() => {
    fetchApplicationData();
  }, [fetchApplicationData]);

  return {
    details,
    setDetails,
    notes,
    documents,
    groupedDocuments,
    isLoading,
    errorMessage,
    refetch: fetchApplicationData,
    setIsLoading,
    setErrorMessage,
  };
};

export default useApplicationDetails;
